import http from '../client';
import { errorHandler } from '../errorHandler';
import type { ApiResponse } from '../types';

export interface AdminGender {
  id: number;
  name: string;
  display_order?: number;

  abbreviation?: string;
  synonyms?: string[];
}

export interface AdminAgeGroup {
  id: number;
  name: string;
  min_age?: number | null;
  max_age?: number | null;
  description?: string;
  display_order?: number;

  abbreviation?: string;
  synonyms?: string[];
}

export interface AdminQuestion {
  id: number;
  question: string;
  default_answer: string;
  category: string;
  display_order?: number;

  abbreviation?: string;
  synonyms?: string[];
}

export interface AdminMistake {
  id: number;
  mistake: string;
  correction: string;
  display_order?: number;

  abbreviation?: string;
  synonyms?: string[];
}

export interface AdminRegion {
  id: number;
  name: string;
  system_tag?: string;
  sub_groups?: string;
  description?: string;
  image_url?: string;
  display_order?: number;

  abbreviation?: string;
  synonyms?: string[];
}

export interface AdminAllergyMaster {
  id: number;
  name: string;
  parent_id?: number | null;
  parent?: { id: number; name: string } | null;
  abbreviation?: string;
  synonyms?: string[];
  details?: string | null;
  is_active: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
}

export interface AdminMedicalCondition {
  id: number;
  name: string;
  description?: string;
  is_active?: boolean;
  display_order?: number;
  parent_id?: number | null;
  parent?: { id: number; name: string } | null;
  abbreviation?: string;
  synonyms?: string[];
}

export interface AdminNaturalCondition {
  id: number;
  name: string;
  description?: string;
  display_order?: number;

  abbreviation?: string;
  synonyms?: string[];
}

export interface AdminDosageUnit {
  id: number;
  name: string;
  abbreviation?: string;
  display_order?: number;

  synonyms?: string[];
}

export interface AdminDosageFrequency {
  id: number;
  name: string;
  times_per_day?: number;
  display_order?: number;

  abbreviation?: string;
  synonyms?: string[];
}

export interface AdminDosageTiming {
  id: number;
  name: string;
  description?: string;
  display_order?: number;

  abbreviation?: string;
  synonyms?: string[];
}

export interface AdminDosageMealTiming {
  id: number;
  name: string;
  description?: string;
  display_order?: number;

  abbreviation?: string;
  synonyms?: string[];
}

export interface AdminDosageDurationUnit {
  id: number;
  name: string;
  display_order?: number;

  abbreviation?: string;
  synonyms?: string[];
}

export interface AdminPreparationTimeUnit {
  id: number;
  name: string;
  display_order?: number;

  abbreviation?: string;
  synonyms?: string[];
}

export interface AdminPublicationType {
  id: number;
  name: string;
  display_order?: number;

  abbreviation?: string;
  synonyms?: string[];
}

export interface AdminReferenceTemplate {
  id: number;
  name: string;
  publication_type_id?: number | null;
  publication_title?: string | null;
  publication_authors?: string | null;
  publication_name?: string | null;
  published_date?: string | null;
  specific_fields?: Record<string, unknown> | null;
  notes?: string | null;
  is_active?: boolean;
  display_order?: number;
  publicationType?: { id: number; name: string } | null;
}

export interface AdminProfileListMaster {
  id: number;
  name: string;
  value?: string | null;
  code?: string | null;
  display_order?: number;

  abbreviation?: string;
  synonyms?: string[];
}

export interface AdminExercise {
  id: number;
  name: string;
  sanskrit_name?: string | null;
  english_name?: string | null;
  hindi_name?: string | null;
  image_url?: string | null;
  parent_id?: number | null;
  category?: 'Pranayam' | 'Yoga' | 'Modern' | 'Other';
  description?: string;
  keywords?: string;
  display_order?: number;

  abbreviation?: string;
  synonyms?: string[];
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  getAll?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

const getQueryString = (params?: PaginationParams & { getAll?: boolean }) => {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.getAll) queryParams.append('getAll', 'true');
  if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  const str = queryParams.toString();
  return str ? `?${str}` : '';
};

export const listManagementApi = {
  // --- Genders ---
  getGenders: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminGender>>> => {
    try {
      const response = await http.get(`/list/genders${getQueryString(params)}`);
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      }; // Response structure matches standard API
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createGender: async (
    data: Omit<AdminGender, 'id'>,
  ): Promise<ApiResponse<AdminGender>> => {
    try {
      const response = await http.post('/list/genders', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateGender: async (
    id: number,
    data: Partial<AdminGender>,
  ): Promise<ApiResponse<AdminGender>> => {
    try {
      const response = await http.put(`/list/genders/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteGender: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/list/genders/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Age Groups ---
  getAgeGroups: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminAgeGroup>>> => {
    try {
      const response = await http.get(
        `/list/age-groups${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createAgeGroup: async (
    data: Omit<AdminAgeGroup, 'id'>,
  ): Promise<ApiResponse<AdminAgeGroup>> => {
    try {
      const response = await http.post('/list/age-groups', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateAgeGroup: async (
    id: number,
    data: Partial<AdminAgeGroup>,
  ): Promise<ApiResponse<AdminAgeGroup>> => {
    try {
      const response = await http.put(`/list/age-groups/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteAgeGroup: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/list/age-groups/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Questions ---
  getQuestions: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminQuestion>>> => {
    try {
      const response = await http.get(
        `/list/questions${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createQuestion: async (
    data: Omit<AdminQuestion, 'id'>,
  ): Promise<ApiResponse<AdminQuestion>> => {
    try {
      const response = await http.post('/list/questions', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateQuestion: async (
    id: number,
    data: Partial<AdminQuestion>,
  ): Promise<ApiResponse<AdminQuestion>> => {
    try {
      const response = await http.put(`/list/questions/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteQuestion: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/list/questions/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Mistakes ---
  getMistakes: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminMistake>>> => {
    try {
      const response = await http.get(
        `/list/mistakes${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createMistake: async (
    data: Omit<AdminMistake, 'id'>,
  ): Promise<ApiResponse<AdminMistake>> => {
    try {
      const response = await http.post('/list/mistakes', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateMistake: async (
    id: number,
    data: Partial<AdminMistake>,
  ): Promise<ApiResponse<AdminMistake>> => {
    try {
      const response = await http.put(`/list/mistakes/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteMistake: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/list/mistakes/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Regions ---
  getRegions: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminRegion>>> => {
    try {
      const response = await http.get(`/list/regions${getQueryString(params)}`);
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createRegion: async (
    data: Omit<AdminRegion, 'id'>,
  ): Promise<ApiResponse<AdminRegion>> => {
    try {
      const response = await http.post('/list/regions', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateRegion: async (
    id: number,
    data: Partial<AdminRegion>,
  ): Promise<ApiResponse<AdminRegion>> => {
    try {
      const response = await http.put(`/list/regions/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteRegion: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/list/regions/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Allergy Master ---
  getAllergyMasters: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminAllergyMaster>>> => {
    try {
      const response = await http.get(
        `/admin/allergy_master${getQueryString(params)}`,
      );
      return {
        success: true,
        data: {
          data: response.data.data,
          pagination: response.data.pagination,
        },
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createAllergyMaster: async (
    data: Omit<AdminAllergyMaster, 'id'>,
  ): Promise<ApiResponse<AdminAllergyMaster>> => {
    try {
      const response = await http.post('/admin/allergy_master', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateAllergyMaster: async (
    id: number,
    data: Partial<AdminAllergyMaster>,
  ): Promise<ApiResponse<AdminAllergyMaster>> => {
    try {
      const response = await http.put(`/admin/allergy_master/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteAllergyMaster: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/admin/allergy_master/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Medical Conditions ---
  getMedicalConditions: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminMedicalCondition>>> => {
    try {
      const response = await http.get(
        `/admin/medical_condition${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createMedicalCondition: async (
    data: Omit<AdminMedicalCondition, 'id'>,
  ): Promise<ApiResponse<AdminMedicalCondition>> => {
    try {
      const response = await http.post('/admin/medical_condition', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateMedicalCondition: async (
    id: number,
    data: Partial<AdminMedicalCondition>,
  ): Promise<ApiResponse<AdminMedicalCondition>> => {
    try {
      const response = await http.put(`/admin/medical_condition/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteMedicalCondition: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/admin/medical_condition/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Natural Conditions ---
  getNaturalConditions: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminNaturalCondition>>> => {
    try {
      const response = await http.get(
        `/list/natural-conditions${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createNaturalCondition: async (
    data: Omit<AdminNaturalCondition, 'id'>,
  ): Promise<ApiResponse<AdminNaturalCondition>> => {
    try {
      const response = await http.post('/admin/natural_condition', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateNaturalCondition: async (
    id: number,
    data: Partial<AdminNaturalCondition>,
  ): Promise<ApiResponse<AdminNaturalCondition>> => {
    try {
      const response = await http.put(`/admin/natural_condition/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteNaturalCondition: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/admin/natural_condition/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Dosage Units ---
  getDosageUnits: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminDosageUnit>>> => {
    try {
      const response = await http.get(
        `/list/dosage_unit${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createDosageUnit: async (
    data: Omit<AdminDosageUnit, 'id'>,
  ): Promise<ApiResponse<AdminDosageUnit>> => {
    try {
      const response = await http.post('/admin/dosage_unit', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateDosageUnit: async (
    id: number,
    data: Partial<AdminDosageUnit>,
  ): Promise<ApiResponse<AdminDosageUnit>> => {
    try {
      const response = await http.put(`/admin/dosage_unit/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteDosageUnit: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/admin/dosage_unit/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Dosage Frequencies ---
  getDosageFrequencies: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminDosageFrequency>>> => {
    try {
      const response = await http.get(
        `/list/dosage_frequency${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createDosageFrequency: async (
    data: Omit<AdminDosageFrequency, 'id'>,
  ): Promise<ApiResponse<AdminDosageFrequency>> => {
    try {
      const response = await http.post('/admin/dosage_frequency', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateDosageFrequency: async (
    id: number,
    data: Partial<AdminDosageFrequency>,
  ): Promise<ApiResponse<AdminDosageFrequency>> => {
    try {
      const response = await http.put(`/admin/dosage_frequency/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteDosageFrequency: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/admin/dosage_frequency/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Dosage Timings ---
  getDosageTimings: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminDosageTiming>>> => {
    try {
      const response = await http.get(
        `/list/dosage_timing${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createDosageTiming: async (
    data: Omit<AdminDosageTiming, 'id'>,
  ): Promise<ApiResponse<AdminDosageTiming>> => {
    try {
      const response = await http.post('/admin/dosage_timing', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateDosageTiming: async (
    id: number,
    data: Partial<AdminDosageTiming>,
  ): Promise<ApiResponse<AdminDosageTiming>> => {
    try {
      const response = await http.put(`/admin/dosage_timing/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteDosageTiming: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/admin/dosage_timing/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Dosage Meal Timings ---
  getDosageMealTimings: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminDosageMealTiming>>> => {
    try {
      const response = await http.get(
        `/list/dosage_meal_timing${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createDosageMealTiming: async (
    data: Omit<AdminDosageMealTiming, 'id'>,
  ): Promise<ApiResponse<AdminDosageMealTiming>> => {
    try {
      const response = await http.post('/admin/dosage_meal_timing', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateDosageMealTiming: async (
    id: number,
    data: Partial<AdminDosageMealTiming>,
  ): Promise<ApiResponse<AdminDosageMealTiming>> => {
    try {
      const response = await http.put(`/admin/dosage_meal_timing/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteDosageMealTiming: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/admin/dosage_meal_timing/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Dosage Duration Units ---
  getDosageDurationUnits: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminDosageDurationUnit>>> => {
    try {
      const response = await http.get(
        `/list/dosage_duration_unit${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createDosageDurationUnit: async (
    data: Omit<AdminDosageDurationUnit, 'id'>,
  ): Promise<ApiResponse<AdminDosageDurationUnit>> => {
    try {
      const response = await http.post('/admin/dosage_duration_unit', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateDosageDurationUnit: async (
    id: number,
    data: Partial<AdminDosageDurationUnit>,
  ): Promise<ApiResponse<AdminDosageDurationUnit>> => {
    try {
      const response = await http.put(
        `/admin/dosage_duration_unit/${id}`,
        data,
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteDosageDurationUnit: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/admin/dosage_duration_unit/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Preparation Time Units ---
  getPreparationTimeUnits: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminPreparationTimeUnit>>> => {
    try {
      const response = await http.get(
        `/list/preparation_time_unit${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createPreparationTimeUnit: async (
    data: Omit<AdminPreparationTimeUnit, 'id'>,
  ): Promise<ApiResponse<AdminPreparationTimeUnit>> => {
    try {
      const response = await http.post('/admin/preparation_time_unit', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updatePreparationTimeUnit: async (
    id: number,
    data: Partial<AdminPreparationTimeUnit>,
  ): Promise<ApiResponse<AdminPreparationTimeUnit>> => {
    try {
      const response = await http.put(
        `/admin/preparation_time_unit/${id}`,
        data,
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deletePreparationTimeUnit: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/admin/preparation_time_unit/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Publication Types ---
  getPublicationTypes: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminPublicationType>>> => {
    try {
      const response = await http.get(
        `/list/publication_type${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createPublicationType: async (
    data: Omit<AdminPublicationType, 'id'>,
  ): Promise<ApiResponse<AdminPublicationType>> => {
    try {
      const response = await http.post('/admin/publication_type', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updatePublicationType: async (
    id: number,
    data: Partial<AdminPublicationType>,
  ): Promise<ApiResponse<AdminPublicationType>> => {
    try {
      const response = await http.put(`/admin/publication_type/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deletePublicationType: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/admin/publication_type/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Reference Templates ---
  getReferenceTemplates: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminReferenceTemplate>>> => {
    try {
      const response = await http.get(
        `/reference-templates/admin${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createReferenceTemplate: async (
    data: Omit<AdminReferenceTemplate, 'id'>,
  ): Promise<ApiResponse<AdminReferenceTemplate>> => {
    try {
      const response = await http.post('/reference-templates/admin', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateReferenceTemplate: async (
    id: number,
    data: Partial<AdminReferenceTemplate>,
  ): Promise<ApiResponse<AdminReferenceTemplate>> => {
    try {
      const response = await http.put(`/reference-templates/admin/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteReferenceTemplate: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/reference-templates/admin/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Exercises ---
  getExercises: async (
    params?: PaginationParams,
  ): Promise<ApiResponse<PaginatedResponse<AdminExercise>>> => {
    try {
      const response = await http.get(
        `/list/exercises${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  /**
   * Fetches ALL exercises in one request (getAll=true) and returns the flat array.
   * Use this to build the exercise type / exercise name hierarchy on the frontend.
   */
  getExercisesAll: async (): Promise<ApiResponse<AdminExercise[]>> => {
    try {
      const response = await http.get('/list/exercises?getAll=true');
      return {
        success: true,
        data: response.data.data as AdminExercise[],
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  /** GET /list/exercise-types â€” returns top-level exercise categories (parent_id IS NULL) */
  getExerciseTypes: async (): Promise<
    ApiResponse<Array<{ id: number; name: string }>>
  > => {
    try {
      const response = await http.get('/list/exercise-types');
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  /** GET /list/pose-types?exerciseTypeId=X â€” returns pose categories under a given exercise type */
  getPoseTypes: async (
    exerciseTypeId: number | string,
  ): Promise<ApiResponse<Array<{ id: number; name: string }>>> => {
    try {
      const response = await http.get(
        `/list/pose-types?exerciseTypeId=${exerciseTypeId}`,
      );
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  /** GET /list/exercises-by-pose?poseTypeId=X â€” returns actual exercise names under a given pose type */
  getExercisesByPose: async (
    poseTypeId: number | string,
  ): Promise<ApiResponse<Array<{ id: number; name: string }>>> => {
    try {
      const response = await http.get(
        `/list/exercises-by-pose?poseTypeId=${poseTypeId}`,
      );
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  /** GET /list/exercise-names?exerciseTypeId=X â€” returns exercise names (grandchildren) under an exercise type, skipping pose level */
  getExerciseNamesByType: async (
    exerciseTypeId: number | string,
  ): Promise<ApiResponse<Array<{ id: number; name: string }>>> => {
    try {
      const response = await http.get(
        `/list/exercise-names?exerciseTypeId=${exerciseTypeId}`,
      );
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createExercise: async (
    data: Omit<AdminExercise, 'id'>,
  ): Promise<ApiResponse<AdminExercise>> => {
    try {
      const response = await http.post('/admin/exercise', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateExercise: async (
    id: number,
    data: Partial<AdminExercise>,
  ): Promise<ApiResponse<AdminExercise>> => {
    try {
      const response = await http.put(`/admin/exercise/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteExercise: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await http.delete(`/admin/exercise/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // --- Profile List Masters (User Gender, Education, Occupation, Ethnicity, Religion, Language) ---
  getUserGenders: async (params?: PaginationParams) => {
    try {
      const response = await http.get(
        `/list/user-genders${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createUserGender: async (data: Omit<AdminProfileListMaster, 'id'>) => {
    try {
      const response = await http.post('/list/user-genders', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateUserGender: async (
    id: number,
    data: Partial<AdminProfileListMaster>,
  ) => {
    try {
      const response = await http.put(`/list/user-genders/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteUserGender: async (id: number) => {
    try {
      const response = await http.delete(`/list/user-genders/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  getEducations: async (params?: PaginationParams) => {
    try {
      const response = await http.get(
        `/list/educations${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createEducation: async (data: Omit<AdminProfileListMaster, 'id'>) => {
    try {
      const response = await http.post('/list/educations', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateEducation: async (
    id: number,
    data: Partial<AdminProfileListMaster>,
  ) => {
    try {
      const response = await http.put(`/list/educations/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteEducation: async (id: number) => {
    try {
      const response = await http.delete(`/list/educations/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  getOccupations: async (params?: PaginationParams) => {
    try {
      const response = await http.get(
        `/list/occupations${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createOccupation: async (data: Omit<AdminProfileListMaster, 'id'>) => {
    try {
      const response = await http.post('/list/occupations', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateOccupation: async (
    id: number,
    data: Partial<AdminProfileListMaster>,
  ) => {
    try {
      const response = await http.put(`/list/occupations/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteOccupation: async (id: number) => {
    try {
      const response = await http.delete(`/list/occupations/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  getEthnicities: async (params?: PaginationParams) => {
    try {
      const response = await http.get(
        `/list/ethnicities${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createEthnicity: async (data: Omit<AdminProfileListMaster, 'id'>) => {
    try {
      const response = await http.post('/list/ethnicities', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateEthnicity: async (
    id: number,
    data: Partial<AdminProfileListMaster>,
  ) => {
    try {
      const response = await http.put(`/list/ethnicities/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteEthnicity: async (id: number) => {
    try {
      const response = await http.delete(`/list/ethnicities/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  getReligions: async (params?: PaginationParams) => {
    try {
      const response = await http.get(
        `/list/religions${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createReligion: async (data: Omit<AdminProfileListMaster, 'id'>) => {
    try {
      const response = await http.post('/list/religions', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateReligion: async (id: number, data: Partial<AdminProfileListMaster>) => {
    try {
      const response = await http.put(`/list/religions/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteReligion: async (id: number) => {
    try {
      const response = await http.delete(`/list/religions/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  getLanguages: async (params?: PaginationParams) => {
    try {
      const response = await http.get(
        `/list/languages${getQueryString(params)}`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  createLanguage: async (data: Omit<AdminProfileListMaster, 'id'>) => {
    try {
      const response = await http.post('/list/languages', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  updateLanguage: async (id: number, data: Partial<AdminProfileListMaster>) => {
    try {
      const response = await http.put(`/list/languages/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  deleteLanguage: async (id: number) => {
    try {
      const response = await http.delete(`/list/languages/${id}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
};
